<template>
  <v-card v-if="editing" class="edit experience mt-2" key="editing" dark>
    <v-card-title v-if="value.id">
      <div class="pt-3">
        <div class="title">{{ value.position }}</div>
        <div class="subheading grey--text">{{ value.organization.name }}</div>
      </div>
    </v-card-title>
    <v-card-title v-else class="title">
      New Work Experience
    </v-card-title>

    <can I="edit" a="Experience">
      <v-card-text>
        <v-form v-model="valid">
          <v-text-field
            label="Position"
            v-model="experience.position"
            required
            :rules="rules.position"
            counter="64"
            :error-messages="(experience.errors && experience.errors.position) || []"
          />
          <v-text-field
            label="Organization"
            v-model="experience.organization.name"
            required
            :error-messages="(experience.errors && experience.errors.organization_name) || []"
          />
          <v-text-field label="Location" v-model="experience.location.address" />
          <v-select label="Type" v-model="experience.type" :items="types" required />
          <date-picker
            label="Starting Date"
            v-model="experience.startsAt"
            required
            :rules="rules.startsAt"
            append-icon="mdi-calendar"
          />
          <date-picker
            label="End Date"
            v-model="experience.endsAt"
            :rules="rules.endsAt"
            clearable
            append-icon="mdi-calendar"
          />
          <v-autocomplete
            multiple
            label="Disciplines"
            v-model="experience.disciplines"
            :items="disciplines"
            return-object
            item-text="name"
          >
            <v-chip slot="selection" slot-scope="{ item: discipline }">
              {{ discipline.name }}
            </v-chip>
          </v-autocomplete>
        </v-form>
        <v-text-field
          label="Add Responsibility"
          v-model="newResponsibility"
          class="mb-0 pb-0"
          append-icon="mdi-plus"
          @click:append="pushResponsibility"
          @keyup.enter="pushResponsibility"
        />
      </v-card-text>

      <v-list v-if="experience.responsibilities && experience.responsibilities[0]" three-line dense>
        <v-subheader class="mt-0">Responsibilities</v-subheader>
        <v-list-tile v-for="(responsibility, i) in experience.responsibilities" :key="i" @click="">
          <v-list-tile-content>
            <v-list-tile-sub-title>
              {{ responsibility }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
          <v-list-tile-action>
            <v-btn flat icon color="grey" @click="removeResponsibility(i)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-list-tile-action>
        </v-list-tile>
      </v-list>

      <v-card-actions>
        <v-spacer />
        <v-btn flat @click="cancel" color="grey">Cancel</v-btn>
        <v-btn color="primary" :disabled="!valid" @click="submit">
          {{ this.experience.id ? 'Update' : 'Create' }} Work Experience
        </v-btn>
      </v-card-actions>
    </can>
  </v-card>

  <v-card v-else class="view experience mt-2" key="viewing">
    <v-card-title>
      <LogoUpload
        v-if="value.organization.id"
        :src="
          value.organization.logo
            ? value.organization.logo
            : '/img/missing/logos/organizations/original.png'
        "
        :path="`/admin/experience_organizations/${value.organization.id}/logo`"
        @success="logoSuccess"
      />
      <v-avatar v-else class="elevation-0 mr-3">
        <img
          :src="
            value.organization.logo
              ? value.organization.logo
              : '/img/missing/logos/organizations/original.png'
          "
        />
      </v-avatar>

      <div class="pt-3">
        <div class="title">{{ experience.position }}</div>
        <div class="subheading grey--text">{{ experience.organization.name }}</div>
      </div>

      <v-spacer />

      <v-tooltip left v-if="hasSupervisor && supervisor.verifiedAt">
        <v-icon slot="activator" color="success">mdi-check-circle</v-icon>
        <span>Supervisor verified</span>
      </v-tooltip>

      <can I="delete" a="Experience">
        <v-tooltip left>
          <v-btn
            slot="activator"
            v-if="experience.id"
            small
            flat
            fab
            color="error"
            @click="destroy"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <span>Remove</span>
        </v-tooltip>
      </can>

      <can I="edit" a="Experience">
        <v-tooltip left>
          <v-btn slot="activator" small flat fab @click="editing = true" color="secondary">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <span>Edit</span>
        </v-tooltip>
      </can>
    </v-card-title>

    <v-divider />

    <!-- Attributes table -->
    <can I="read" a="Experience">
      <table class="v-table theme--dark">
        <tbody>
          <tr v-if="experience.id">
            <th class="text-xs-right">ID</th>
            <td>{{ experience.id }}</td>
          </tr>

          <tr v-if="experience.organization">
            <th class="text-xs-right">Organization</th>
            <td v-if="experience.organization.id">
              {{ experience.organization.name }}
            </td>
            <td v-else>{{ experience.organization.name }}</td>
          </tr>

          <tr>
            <th class="text-xs-right">Position or Title</th>
            <td>{{ experience.position }}</td>
          </tr>
          <tr>
            <th class="text-xs-right">Location</th>
            <td v-if="experience.location.address">{{ experience.location.address }}</td>
            <td v-else><em>Unknown</em></td>
          </tr>

          <tr>
            <th class="text-xs-right">Experience Type</th>
            <td>{{ experience.type }}</td>
          </tr>

          <tr>
            <th class="text-xs-right">Disciplines</th>
            <td v-if="experience.disciplines.length">
              <span v-for="(discipline, i) in experience.disciplines" :key="`discipline-${i}`">
                &laquo; {{ discipline.name }} &raquo;
              </span>
            </td>
            <td v-else><em>None</em></td>
          </tr>

          <tr>
            <th class="text-xs-right">Experience Responsibilities</th>
            <td>Has {{ experience.responsibilities ? experience.responsibilities.length : 0 }}</td>
          </tr>

          <tr>
            <th class="text-xs-right">Started</th>
            <td>{{ new Date(experience.startsAt) | date('MMMM do yyyy') }}</td>
          </tr>

          <tr>
            <th class="text-xs-right">Ended</th>
            <td v-if="experience.endsAt">
              {{ new Date(experience.endsAt) | date('MMMM do yyyy') }}
            </td>
            <td v-else><em>Currently active</em></td>
          </tr>
          <!-- Supervisor section -->
          <can I="read" a="Supervisor" v-if="hasSupervisor">
            <tr>
              <th class="text-xs-right">Supervisor</th>
              <td>
                {{ supervisor.name }}
                ({{ supervisor.position }} at {{ experience.organization.name }}) <br />
                {{ supervisor.email }}
              </td>
            </tr>
            <tr v-if="supervisor.verificationSentAt">
              <th class="text-xs-right">Notified</th>
              <td>
                {{ new Date(supervisor.verificationSentAt) | date('h:mm a, MMMM do yyyy') }}
                ({{ new Date(supervisor.verificationSentAt) | inWords({ addSuffix: true }) }})
              </td>
            </tr>
            <tr v-if="supervisor.verifiedAt">
              <th class="text-xs-right">Verified</th>
              <td>
                {{ new Date(supervisor.verifiedAt) | date('h:mm a, MMMM do yyyy') }}
                ({{ new Date(supervisor.verifiedAt) | inWords({ addSuffix: true }) }})
              </td>
            </tr>
          </can>
        </tbody>
      </table>
    </can>
  </v-card>
</template>

<script>
// Components
import LogoUpload from '@/components/LogoUpload'

// Helpers
import { mapGetters } from 'vuex'

// Data
import types from '@/data/experience_types'

// Vue Component
export default {
  components: { LogoUpload },

  props: { value: Object },

  data() {
    return {
      // Data
      types,
      // Toggles
      editing: this.value && this.value.id === null,
      // Models
      experience: JSON.parse(JSON.stringify(this.value)),
      newResponsibility: '',
      // Validations
      valid: this.value && this.value.id !== null,
      rules: {
        position: [
          v => !!v || 'must be specified',
          v => v.length <= 64 || 'must not exceed 64 characters',
        ],
        organization: [v => !!v || 'must be specified'],
        type: [v => !!v || 'must be specified'],
        startsAt: [v => !!v || 'must be specified'],
        endsAt: [],
      },
    }
  },

  computed: {
    ...mapGetters({
      disciplines: 'disciplines/all',
      organizations: 'organizations/all',
      organizationsLoading: 'organizations/loading',
      institutions: 'institutions/all',
    }),
    hasSupervisor() {
      return this.experience.supervisors && this.experience.supervisors.length > 0
    },
    supervisor() {
      return this.hasSupervisor ? this.experience.supervisors[0] : {}
    },
  },

  methods: {
    logoSuccess() {
      this.$store.commit('snackbar', {
        text: 'Logo uploaded!',
        type: 'success',
      })
      this.$emit('refresh')
    },

    pushResponsibility() {
      if (!this.experience.responsibilities) this.$set(this.experience, 'responsibilities', [])
      this.experience.responsibilities.push(this.newResponsibility)
      this.newResponsibility = ''
    },

    removeResponsibility(i) {
      if (this.experience.responsibilities) this.experience.responsibilities.splice(i, 1)
    },

    setOrganization(id) {
      this.experience.organization.name = this.$store.getters['organizations/find'](id).name
    },

    cancel() {
      this.editing = false
      this.$emit('refresh')
    },

    submit() {
      if (this.experience.id) {
        this.$store
          .dispatch('admin/experiences/update', this.experience)
          .then(() => (this.editing = !!this.experience.errors))
      } else {
        this.$store
          .dispatch('admin/experiences/create', this.experience)
          .then(() => (this.editing = !!this.experience.errors))
      }
    },

    destroy() {
      this.$store
        .dispatch('admin/experiences/destroy', this.experience.id)
        .then(() => this.$emit('refresh'))
    },
  },
}
</script>
